import { useRef, useState, useEffect, useCallback } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

// Reconnaissance vocale disponible ? (Chrome, Edge, Safari desktop + mobile).
export const isVoiceSupported = !!SR;

// Ajoute la dictée à la SUITE du texte existant (sans jamais l'écraser).
export const appendTranscript = (prev, chunk) => {
  const base = prev || "";
  if (!chunk) return base;
  const sep = base && !/\s$/.test(base) ? " " : "";
  return base + sep + chunk;
};

/**
 * Bouton micro léger basé sur l'API native du navigateur (Web Speech API).
 * - Aucun coût, aucune clé, aucun backend, aucun audio conservé.
 * - value / onChange : le champ contrôlé. La dictée s'insère EN DIRECT dans le champ
 *   pendant que l'on parle (comme une dictée smartphone), à la suite du texte existant.
 * - Se masque tout seul si la reconnaissance vocale n'est pas disponible (ex. Firefox).
 */
export function MicButton({ value = "", onChange, lang = "fr-FR", className = "", testid = "mic-button" }) {
  const recRef = useRef(null);
  const valueRef = useRef(value);
  const baseRef = useRef("");
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  valueRef.current = value;

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch (_) { /* noop */ }
    setListening(false); setTranscribing(false);
  }, []);

  useEffect(() => () => { try { recRef.current?.stop(); } catch (_) { /* noop */ } }, []);

  const start = useCallback(() => {
    if (!SR) return;
    let rec;
    try { rec = new SR(); } catch (_) { return; }
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    // Point de départ : le texte déjà présent dans le champ au lancement de la dictée.
    baseRef.current = valueRef.current || "";
    rec.onresult = (e) => {
      let finalT = "", interimT = "";
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        const t = r[0]?.transcript || "";
        if (r.isFinal) finalT += t; else interimT += t;
      }
      setTranscribing(!!interimT);
      const spoken = (finalT + interimT).replace(/\s+/g, " ").trim();
      onChange(appendTranscript(baseRef.current, spoken));
    };
    rec.onerror = (ev) => {
      const err = ev?.error;
      if (err === "not-allowed" || err === "service-not-allowed") {
        toast.error("Micro non autorisé. Autorisez l'accès au micro dans votre navigateur pour dicter.");
      } else if (err && err !== "no-speech" && err !== "aborted") {
        toast.error("La dictée vocale a été interrompue. Vous pouvez réessayer.");
      }
      setListening(false); setTranscribing(false);
    };
    rec.onend = () => { setListening(false); setTranscribing(false); };
    recRef.current = rec;
    try { rec.start(); setListening(true); }
    catch (_) { /* déjà démarré */ }
  }, [lang, onChange]);

  if (!SR) return null;

  return (
    <button
      type="button"
      data-testid={testid}
      aria-label={listening ? "Arrêter la dictée" : "Dicter au micro"}
      aria-pressed={listening}
      title={listening ? "Arrêter la dictée" : "Dicter au micro"}
      onClick={() => (listening ? stop() : start())}
      className={`mic-btn ${listening ? "mic-btn--live" : ""} ${className}`}
    >
      {listening ? (
        <>
          <span className="mic-dot" aria-hidden="true" />
          <span className="mic-label">{transcribing ? "Transcription…" : "Écoute en cours…"}</span>
        </>
      ) : (
        <Mic size={16} />
      )}
    </button>
  );
}

export default MicButton;
